/**
 * 発注画面の現場選択肢構築ユーティリティ
 *
 * 設計書 05-frontend-design.md §5.1 / §5.3 / 07-permissions.md §3 整合。
 *
 * MCM/DEV(管理者): フィルタ・登録ともに items 由来の全現場(現場マスタ API 提供後に切替予定)
 * SITE_*(サイト制限): 登録は自サイト固定、フィルタは固定ラベル表示
 *
 * 公開 API は `buildSiteSelectionContext` 1 つに集約し、`{ registrationOptions,
 * fixedRegistrationSiteId, filterMode }` を一括返却する(呼出側の整合保証を utils 内に閉じる)。
 */
import type { User } from '@/features/auth/types/auth.types';
import type {
  PurchaseOrderViewModel,
  SiteFilterMode,
  SiteOption,
  SiteSelectionContext,
} from '../types/purchaseOrder';
import { isAdmin } from '@/features/auth/utils/permissionUtils';

/** 現場名が取得できない場合の表示用ラベル接頭辞 */
const UNNAMED_SITE_LABEL_PREFIX = '現場#';

/**
 * `user.siteId` 値オブジェクトの primitive 値を取得
 *
 * `user.siteId` は値オブジェクト(`{ toPrimitive: () => number }`)/ プリミティブ / null / undefined の
 * 4 形態を取りうる。既存型定義との整合と段階的移行のため、いずれの形態にも対応する。
 */
function resolveSiteIdPrimitive(params: {
  readonly user: User | null;
}): number | null {
  const { user } = params;
  if (user === null) {
    return null;
  }
  const { siteId } = user;
  if (siteId === undefined || siteId === null) {
    return null;
  }
  if (typeof siteId === 'number') {
    return siteId;
  }
  return siteId.toPrimitive();
}

/**
 * 管理者(MCM/DEV)用: 現場マスタから取得した全現場をそのまま選択肢化
 *
 * `accessibleSites` は `useAccessibleSiteOptions` フックが `GET /api/sites` から取得した
 * 全現場(MCM/DEV はアクセス可全現場)を value/label に変換済の配列。
 */
function buildAdminSiteOptions(params: {
  readonly accessibleSites: readonly SiteOption[];
}): readonly SiteOption[] {
  return params.accessibleSites;
}

/**
 * SITE_*用: 自サイトのみの単一選択肢
 *
 * `user.site.siteName` を一次情報源、items 内の同 siteId を二次フォールバック、
 * いずれも取得できなければ `現場#<id>` のフォールバックラベルを使用する。
 */
function buildOwnSiteOption(params: {
  readonly user: User;
  readonly userSiteIdPrimitive: number;
  readonly items: readonly PurchaseOrderViewModel[];
}): SiteOption {
  const { user, userSiteIdPrimitive, items } = params;
  const primarySiteName = user.site?.siteName;
  if (primarySiteName !== undefined) {
    return { value: userSiteIdPrimitive, label: primarySiteName };
  }
  const fallbackFromItems = items.find(
    (item) => item.siteId === userSiteIdPrimitive
  );
  return {
    value: userSiteIdPrimitive,
    label:
      fallbackFromItems?.siteName ??
      `${UNNAMED_SITE_LABEL_PREFIX}${userSiteIdPrimitive}`,
  };
}

/**
 * SITE_*用の固定現場名ラベル(自サイト名 / `現場#<id>` フォールバック)
 */
function resolveFixedSiteLabel(params: {
  readonly user: User;
  readonly userSiteIdPrimitive: number;
}): string {
  const primarySiteName = params.user.site?.siteName;
  if (primarySiteName !== undefined) {
    return primarySiteName;
  }
  return `${UNNAMED_SITE_LABEL_PREFIX}${params.userSiteIdPrimitive}`;
}

/**
 * 発注画面の現場関連派生情報を一括構築
 *
 * `isAdmin(user)` で内部判定する。
 * - 管理者(MCM/DEV): `accessibleSites`(現場マスタ API 由来の全現場)から構築
 * - SITE_*: `user.site` + items フォールバックで自サイトのみ
 *
 * 結果は `SiteSelectionContext` 構造体で返却し、登録モーダル用とフィルタ用の整合を保証する。
 */
export function buildSiteSelectionContext(params: {
  readonly user: User | null;
  readonly accessibleSites: readonly SiteOption[];
  readonly items: readonly PurchaseOrderViewModel[];
}): SiteSelectionContext {
  const { user, accessibleSites, items } = params;
  const isUserAdmin = isAdmin(user);

  if (isUserAdmin) {
    const adminOptions = buildAdminSiteOptions({ accessibleSites });
    const filterMode: SiteFilterMode = { kind: 'selectable', options: adminOptions };
    return {
      registrationOptions: adminOptions,
      fixedRegistrationSiteId: null,
      filterMode,
    };
  }

  // SITE_* (非管理者) の場合
  if (user === null) {
    return {
      registrationOptions: [],
      fixedRegistrationSiteId: null,
      filterMode: { kind: 'hidden' },
    };
  }

  const userSiteIdPrimitive = resolveSiteIdPrimitive({ user });
  if (userSiteIdPrimitive === null) {
    return {
      registrationOptions: [],
      fixedRegistrationSiteId: null,
      filterMode: { kind: 'hidden' },
    };
  }

  const ownOption = buildOwnSiteOption({ user, userSiteIdPrimitive, items });
  const fixedLabel = resolveFixedSiteLabel({ user, userSiteIdPrimitive });
  return {
    registrationOptions: [ownOption],
    fixedRegistrationSiteId: userSiteIdPrimitive,
    filterMode: { kind: 'fixed', label: fixedLabel },
  };
}
